import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Volume2, VolumeX, X } from "lucide-react";
import { ASSETS, ZONES, ZONE_KEEPER, ROULETTE, countdownForRound, isMuted, setMuted } from "../../../utils/minicup";

/**
 * APL Penalty Cup — Penalty Roulette.
 * MOBILE-FIRST. Test on a real phone (iOS Safari + Android Chrome / WebView).
 *
 * Contract is unchanged (drop-in replacement):
 *   props:    { team, sponsor, sound, onEnd, onSponsorClick }
 *   onEnd:    (goals:number, durationMs:number, shotLog:Array) => void
 *   sponsor:  { sponsor_name, logo_url, click_url }
 *   team:     { name, flag_url, code }
 *
 * What changed vs the previous build:
 *   1. Proper goal — crisp white posts + crossbar + real net grid + shallow depth,
 *      drawn in goal-pixel space so strokes never distort (no preserveAspectRatio="none").
 *   2. "TOO SLOW" now ENDS the match (like a save). The countdown finally has stakes.
 *   3. Stadium can never show the transparent checkerboard — a sky→pitch gradient sits
 *      under the (object-cover) stadium image as a guaranteed backstop.
 *   4. Sponsor compositing fixed — in-pitch logo is hidden during the splash, splash is
 *      fully opaque, and the in-game logo is a tidy ad-board chip (not a floating image).
 *   5. Every sponsor/keeper <img> has an onError guard so a bad URL/asset never shows
 *      a broken-image box.
 *
 * NOTE on keeper art: the 5 poses must be ONE consistent character as TRUE transparent
 * PNGs (RGBA, alpha=0 background). keeper_catch_high / keeper_catch_low currently ship as
 * opaque RGB with a checkerboard baked in — regenerate them (see the Emergent brief).
 */

const COL = { TL: 0, BL: 0, TC: 1, BC: 1, TR: 2, BR: 2 };
const isTop = (z) => z[0] === "T";
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// Inline SVG football (no Lottie — Lottie failed on mobile).
function SoccerBall() {
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full minicup-ball-spin" style={{ filter: "drop-shadow(0 4px 5px rgba(0,0,0,.4))" }}>
      <circle cx="50" cy="50" r="47" fill="#fff" stroke="#111" strokeWidth="3" />
      <polygon points="50,30 63,40 58,56 42,56 37,40" fill="#111" />
      <polygon points="50,12 60,20 50,28 40,20" fill="#1a1a1a" />
      <polygon points="20,40 33,38 35,53 24,60 14,50" fill="#1a1a1a" />
      <polygon points="80,40 67,38 65,53 76,60 86,50" fill="#1a1a1a" />
      <polygon points="34,78 42,64 58,64 66,78 50,86" fill="#1a1a1a" />
    </svg>
  );
}

/**
 * Goal frame. Drawn in goal-pixel space (viewBox = goal size, 1:1 with on-screen px)
 * so posts/crossbar stay crisp and uniform at any screen size — no stretch distortion.
 */
function GoalFrame({ g }) {
  const W = g.goalW, H = g.goalH;
  const pt = clamp(W * 0.021, 7, 13);                     // post / crossbar thickness
  const ox0 = pt, ox1 = W - pt, oy0 = pt, oy1 = H;        // front opening (open at the base)
  const dx = W * 0.055, dy = H * 0.12;                    // depth offset — net recedes up + in
  const bx0 = ox0 + dx, bx1 = ox1 - dx, by0 = oy0 + dy;   // back-top opening

  const vN = 12, hN = 8;
  const vlines = [], hlines = [], blines = [];
  for (let i = 1; i < vN; i++) vlines.push(ox0 + ((ox1 - ox0) * i) / vN);
  for (let i = 1; i < hN; i++) hlines.push(oy0 + ((oy1 - oy0) * i) / hN);
  for (let i = 1; i < vN; i++) blines.push(bx0 + ((bx1 - bx0) * i) / vN);

  return (
    <svg viewBox={`0 0 ${W} ${H}`}
      style={{ position: "absolute", left: g.goalLeft, top: g.goalTop, width: W, height: H, pointerEvents: "none", zIndex: 2, overflow: "visible" }}
      data-testid="game-goal-frame">
      <defs>
        <filter id="goalshadow" x="-12%" y="-12%" width="124%" height="135%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#000" floodOpacity="0.35" />
        </filter>
      </defs>

      {/* net — front mesh */}
      <g stroke="#fff" strokeOpacity="0.22" strokeWidth="1" shapeRendering="crispEdges">
        {vlines.map((x, i) => <line key={`v${i}`} x1={x} y1={oy0} x2={x} y2={oy1} />)}
        {hlines.map((y, i) => <line key={`h${i}`} x1={ox0} y1={y} x2={ox1} y2={y} />)}
      </g>

      {/* net — back mesh + depth connectors (subtle, gives the goal a "box" read) */}
      <g stroke="#fff" strokeOpacity="0.11" strokeWidth="1">
        {blines.map((x, i) => <line key={`b${i}`} x1={x} y1={by0} x2={x} y2={oy1} />)}
        <line x1={bx0} y1={by0} x2={bx1} y2={by0} />
        <line x1={ox0} y1={oy0} x2={bx0} y2={by0} />
        <line x1={ox1} y1={oy0} x2={bx1} y2={by0} />
        <line x1={bx0} y1={by0} x2={bx0} y2={oy1} />
        <line x1={bx1} y1={by0} x2={bx1} y2={oy1} />
      </g>

      {/* frame: crossbar + two posts */}
      <g filter="url(#goalshadow)">
        <rect x={0} y={0} width={W} height={pt} rx={pt / 2} fill="#fff" />
        <rect x={0} y={0} width={pt} height={H} rx={pt / 2} fill="#fff" />
        <rect x={W - pt} y={0} width={pt} height={H} rx={pt / 2} fill="#fff" />
      </g>

      {/* inner edge shading — a touch of 3D on the frame */}
      <g fill="none" stroke="#c9d2dc" strokeWidth="1" strokeOpacity="0.7">
        <line x1={pt - 0.5} y1={pt} x2={pt - 0.5} y2={H} />
        <line x1={W - pt + 0.5} y1={pt} x2={W - pt + 0.5} y2={H} />
        <line x1={0} y1={pt - 0.5} x2={W} y2={pt - 0.5} />
      </g>
    </svg>
  );
}

export default function PenaltyGame({ team, sponsor, sound, onEnd, onSponsorClick }) {
  const wrapRef = useRef(null);
  const ballRef = useRef(null);

  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [pose, setPose] = useState("idle");
  const [keeperZone, setKeeperZone] = useState(null);   // null = idle centre
  const [userZone, setUserZone] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [flash, setFlash] = useState(false);
  const [cdPct, setCdPct] = useState(1);
  const [muted, setMutedState] = useState(isMuted());
  const [splash, setSplash] = useState(true);
  const [ready, setReady] = useState(false);

  const geo = useRef({ ready: false });
  const phaseRef = useRef("idle");      // idle | countdown | reveal | over
  const roundRef = useRef(0);
  const goalsRef = useRef(0);
  const keeperZoneRef = useRef(null);
  const startTs = useRef(0);
  const shots = useRef([]);
  const endedRef = useRef(false);
  const startedRef = useRef(false);
  const cdTimer = useRef(null);
  const tickTimer = useRef(null);
  const cdStart = useRef(0);
  const cdDur = useRef(2000);
  const revealTimers = useRef([]);

  const flag = team?.flag_url;

  // preload art once for instant pose swaps
  useEffect(() => {
    [ASSETS.stadium, ...Object.values(ASSETS.keeper)].forEach((src) => { const i = new Image(); i.src = src; });
  }, []);

  const computeGeo = () => {
    const el = wrapRef.current; if (!el) return;
    const W = el.clientWidth, H = el.clientHeight;
    const goalW = Math.min(W * 0.9, H * 0.78, 540);
    const goalH = goalW * 0.42;
    const goalLeft = (W - goalW) / 2;
    const pitchTop = H * 0.5;                       // foreground grass starts here
    const goalBottom = pitchTop + goalH * 0.18;     // goal base planted in the grass
    const goalTop = goalBottom - goalH;
    const colW = goalW / 3, rowH = goalH / 2;
    const keeperH = goalH * 0.82;                   // fits inside the goal mouth
    const keeperW = keeperH * 0.72;
    const ballSize = clamp(W * 0.15, 50, 74);
    const ballHome = { x: W / 2, y: clamp(goalBottom + (H - goalBottom) * 0.55, goalBottom + 40, H - ballSize * 0.6) };
    geo.current = { ready: true, W, H, goalW, goalH, goalLeft, goalTop, goalBottom, colW, rowH, keeperW, keeperH, ballSize, ballHome, pitchTop };
    setReady(true);
  };

  useLayoutEffect(() => {
    computeGeo();
    const ro = new ResizeObserver(computeGeo);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // splash → kick off, music start, hard audio-stop on unmount (leak guard)
  // Begin play once: driven by the splash CSS animation end (compositor — robust
  // against timer throttling) with a setTimeout fallback. Guarded to run once.
  const beginPlay = () => {
    if (startedRef.current || endedRef.current) return;
    startedRef.current = true;
    setSplash(false);
    startRound();
  };

  useEffect(() => {
    endedRef.current = false;     // reset on (re)mount — StrictMode cleanup sets it true
    startedRef.current = false;
    startTs.current = Date.now();
    const t = setTimeout(beginPlay, 1700);   // fallback if animationend doesn't fire
    try { sound.startMusic(); } catch (e) {}
    return () => {
      clearTimeout(t);
      endedRef.current = true;
      clearTimers();
      sound.stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoneCenter = (z) => {
    const g = geo.current;
    return { x: g.goalLeft + g.colW * (COL[z] + 0.5), y: g.goalTop + g.rowH * (isTop(z) ? 0.5 : 1.5) };
  };

  const keeperBox = () => {
    const g = geo.current;
    if (!keeperZone) return { left: g.goalLeft + g.goalW / 2 - g.keeperW / 2, top: g.goalBottom - g.keeperH };
    const cx = g.goalLeft + g.colW * (COL[keeperZone] + 0.5);
    const left = Math.max(g.goalLeft - g.keeperW * 0.1, Math.min(g.goalLeft + g.goalW - g.keeperW * 0.9, cx - g.keeperW / 2));
    let top;
    if (pose === "catch_high") top = g.goalTop - g.keeperH * 0.06;
    else if (pose === "catch_low") top = g.goalBottom - g.keeperH * 0.6;
    else top = isTop(keeperZone) ? g.goalTop - g.keeperH * 0.02 : g.goalBottom - g.keeperH * 0.86;
    return { left, top };
  };

  const placeBallHome = (instant = true) => {
    const el = ballRef.current; if (!el) return;
    if (instant) el.style.transition = "none";
    el.style.transform = "translate(-50%, -50%) scale(1)";
    if (instant) requestAnimationFrame(() => { if (el) el.style.transition = ""; });
  };
  const animateBallTo = (z) => {
    const el = ballRef.current, g = geo.current; if (!el) return;
    const c = zoneCenter(z);
    const dx = c.x - g.ballHome.x, dy = c.y - g.ballHome.y;
    el.style.transition = `transform ${ROULETTE.ballTravelMs}ms cubic-bezier(.2,.7,.3,1)`;
    el.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.55)`;
  };

  const clearTimers = () => {
    if (cdTimer.current) clearTimeout(cdTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    revealTimers.current.forEach((t) => clearTimeout(t));
    revealTimers.current = [];
    cdTimer.current = null; tickTimer.current = null;
  };

  const startRound = () => {
    if (endedRef.current) return;
    clearTimers();
    roundRef.current += 1;
    setRound(roundRef.current);
    phaseRef.current = "countdown";
    setUserZone(null);
    setKeeperZone(null);
    setPose("idle");
    setOverlay(null);
    keeperZoneRef.current = ZONES[Math.floor(Math.random() * ZONES.length)];
    placeBallHome(true);
    const dur = countdownForRound(roundRef.current);
    cdDur.current = dur; cdStart.current = Date.now();
    setCdPct(1);
    tickTimer.current = setInterval(() => {
      setCdPct(Math.max(0, 1 - (Date.now() - cdStart.current) / cdDur.current));
    }, 50);
    cdTimer.current = setTimeout(onExpire, dur);
  };

  // Ran out of time → the run is OVER (consistent with a save). Time now has stakes.
  const onExpire = () => {
    if (endedRef.current || phaseRef.current !== "countdown") return;
    clearTimers();
    phaseRef.current = "over";
    setCdPct(0);
    setOverlay("TOO SLOW");
    shots.current.push({ zone: null, keeper: keeperZoneRef.current, result: "miss" });
    sound.play("save");
    revealTimers.current.push(setTimeout(finishMatch, ROULETTE.saveHoldMs));
  };

  const onZone = (z) => {
    if (phaseRef.current !== "countdown" || endedRef.current) return;
    clearTimers();
    phaseRef.current = "reveal";
    setUserZone(z);
    setCdPct(0);
    sound.play("kick");
    animateBallTo(z);
    revealTimers.current.push(setTimeout(() => {
      if (endedRef.current) return;
      const kz = keeperZoneRef.current;
      setPose(ZONE_KEEPER[kz]);
      setKeeperZone(kz);
    }, Math.max(0, ROULETTE.ballTravelMs - 220)));
    revealTimers.current.push(setTimeout(() => resolve(z), ROULETTE.ballTravelMs + 180));
  };

  const resolve = (z) => {
    if (endedRef.current) return;
    const saved = z === keeperZoneRef.current;
    shots.current.push({ zone: z, keeper: keeperZoneRef.current, result: saved ? "save" : "goal" });
    if (saved) {
      sound.play("save");
      phaseRef.current = "over";
      setOverlay("SAVED!");
      revealTimers.current.push(setTimeout(finishMatch, ROULETTE.saveHoldMs));
    } else {
      goalsRef.current += 1;
      setScore(goalsRef.current);
      sound.play("goal");
      setOverlay("GOAL");
      setFlash(true);
      revealTimers.current.push(setTimeout(() => setFlash(false), 200));
      revealTimers.current.push(setTimeout(() => { if (!endedRef.current) startRound(); }, ROULETTE.goalFlashMs));
    }
  };

  const finishMatch = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    phaseRef.current = "over";
    clearTimers();
    sound.stopMusic();
    onEnd(goalsRef.current, Date.now() - (startTs.current || Date.now()), shots.current);
  };

  const toggleMute = () => {
    const v = !muted;
    setMutedState(v);
    setMuted(v);
    if (v) sound.stopAll();
    else sound.startMusic();
  };

  const sponsorLogoClick = async () => {
    if (!sponsor) return;
    if (onSponsorClick) await onSponsorClick("goal_post");
    if (sponsor.click_url) { try { window.open(sponsor.click_url, "_blank", "noopener"); } catch {} }
  };

  const footerText = sponsor ? `APL Penalty Cup · Powered by ${sponsor.sponsor_name}` : "APL Penalty Cup";
  const g = geo.current;
  const kb = ready ? keeperBox() : { left: 0, top: 0 };

  return (
    <div className="football-theme fixed inset-0 z-[60] flex w-full flex-col bg-slate-950" style={{ height: "100dvh" }} data-testid="penalty-game">
      {/* header */}
      <div className="flex items-center justify-between px-3" style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))", paddingBottom: "0.5rem" }}>
        <button onClick={finishMatch} className="flex min-h-[44px] items-center gap-1.5 px-1 text-sm font-bold text-white/90" data-testid="game-exit-btn">
          <X size={18} /> End
        </button>
        <div className="flex items-center gap-2">
          {flag && <img src={flag} alt="" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-5 w-7 rounded object-cover" />}
          <span className="text-sm font-bold text-white">{team?.name}</span>
        </div>
        <button onClick={toggleMute} className="flex min-h-[44px] min-w-[44px] items-center justify-center text-white/90" data-testid="game-mute-btn" aria-label={muted ? "unmute" : "mute"}>
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>

      {/* score */}
      <div className="text-center">
        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300/80">Goals · Round {round || 1}</div>
        <div className="text-5xl font-black leading-none text-white drop-shadow" data-testid="game-score">{score}</div>
      </div>

      {/* play area — sky→pitch gradient backstop guarantees no transparent checkerboard,
          the stadium image (object-cover) sits on top when it loads */}
      <div
        ref={wrapRef}
        className="relative mt-2 flex-1 select-none overflow-hidden"
        style={{ touchAction: "manipulation", background: "linear-gradient(180deg,#bfe3ff 0%,#a9d7ff 36%,#6fc24a 36%,#4ea537 100%)" }}
        data-testid="game-canvas"
      >
        <img src={ASSETS.stadium} alt="" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 h-full w-full object-cover" style={{ zIndex: 0 }} />

        {/* foreground pitch — guarantees the goal, keeper and ball stand on grass (not sky) */}
        {ready && (
          <div className="absolute left-0 right-0 bottom-0" style={{ top: g.pitchTop, zIndex: 1, background: "linear-gradient(180deg,#62bd3f 0%,#4ea537 55%,#3c8a28 100%)" }}>
            <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "6%", width: "82%", height: "62%", border: "2px solid rgba(255,255,255,.35)", borderTop: "none", borderRadius: "0 0 14px 14px" }} />
          </div>
        )}

        {ready && !splash && (
          <>
            {/* countdown bar */}
            <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 6, width: g.goalW, zIndex: 5 }}>
              <div className="h-2 w-full overflow-hidden rounded-full bg-black/40">
                <div className="h-full rounded-full transition-[width] duration-75 ease-linear"
                  style={{ width: `${cdPct * 100}%`, background: cdPct > 0.4 ? "#f5c542" : "#ef4444" }} data-testid="game-countdown" />
              </div>
            </div>

            <GoalFrame g={g} />

            {/* keeper */}
            <img src={ASSETS.keeper[pose]} alt="keeper" draggable={false}
              onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              style={{ position: "absolute", left: kb.left, top: kb.top, width: g.keeperW, height: g.keeperH, objectFit: "contain", objectPosition: "center bottom", pointerEvents: "none", zIndex: 3, transition: "left .22s ease-out, top .22s ease-out", filter: "drop-shadow(0 5px 4px rgba(0,0,0,.28))" }}
              data-testid="game-keeper" />

            {/* 6 tap zones — clear bordered targets with a centre ring so it reads as tappable */}
            {ZONES.map((z) => {
              const left = g.goalLeft + g.colW * COL[z];
              const top = g.goalTop + g.rowH * (isTop(z) ? 0 : 1);
              const active = userZone === z;
              const ring = Math.min(g.colW, g.rowH) * 0.32;
              return (
                <button key={z} onPointerDown={() => onZone(z)} aria-label={`zone ${z}`} data-testid={`game-zone-${z}`}
                  style={{ position: "absolute", left, top, width: g.colW, height: g.rowH, zIndex: 4, display: "flex", alignItems: "center", justifyContent: "center", background: active ? "rgba(245,197,66,.34)" : "rgba(255,255,255,.06)", border: `2px solid rgba(255,255,255,${active ? 0.98 : 0.5})`, boxSizing: "border-box", transition: "background .12s, border-color .12s" }}>
                  <span style={{ width: ring, height: ring, borderRadius: 999, border: `2px solid rgba(255,255,255,${active ? 1 : 0.65})`, background: active ? "rgba(245,197,66,.8)" : "rgba(255,255,255,.12)", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                </button>
              );
            })}

            {/* tap hint (first round, until first shot) */}
            {round <= 1 && !userZone && phaseRef.current === "countdown" && (
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs font-bold text-white"
                style={{ top: g.goalBottom + 8, zIndex: 5 }} data-testid="game-hint">
                Tap a zone to beat the keeper!
              </div>
            )}

            {/* sponsor — tidy ad-board chip at the goal base (Surface C) */}
            {sponsor?.logo_url && (
              <button onClick={sponsorLogoClick} aria-label="sponsor" data-testid="game-sponsor-logo"
                style={{ position: "absolute", left: g.goalLeft + g.goalW / 2, top: g.goalBottom + 10, transform: "translateX(-50%)", zIndex: 5, display: "flex", alignItems: "center", padding: "3px 9px", borderRadius: 8, background: "rgba(2,6,23,0.55)", border: "1px solid rgba(255,255,255,0.16)", cursor: "pointer" }}>
                <img src={sponsor.logo_url} alt="" draggable={false}
                  onError={(e) => { const p = e.currentTarget.parentElement; if (p) p.style.display = "none"; }}
                  style={{ maxHeight: 22, maxWidth: 96, objectFit: "contain" }} />
              </button>
            )}

            {/* ball */}
            <div ref={ballRef} style={{ position: "absolute", left: g.ballHome.x, top: g.ballHome.y, width: g.ballSize, height: g.ballSize, transform: "translate(-50%, -50%)", willChange: "transform", pointerEvents: "none", zIndex: 6 }} data-testid="game-ball">
              <SoccerBall />
            </div>
          </>
        )}

        {/* goal flash */}
        {flash && <div className="pointer-events-none absolute inset-0" style={{ background: "rgba(255,255,255,.55)", zIndex: 7 }} />}

        {/* GOAL / SAVED! / TOO SLOW */}
        {overlay && !splash && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ zIndex: 8 }}>
            <span className={`text-6xl font-black drop-shadow-lg ${overlay === "GOAL" ? "text-amber-400" : overlay === "SAVED!" ? "text-red-400" : "text-orange-300"}`} style={{ animation: "pop .4s ease" }}>{overlay}</span>
          </div>
        )}

        {/* splash — fully opaque; nothing else from the pitch shows through (Surface B) */}
        {splash && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950" style={{ zIndex: 9, animation: "minicupSplashFade 1.5s ease forwards" }} onAnimationEnd={beginPlay} data-testid="game-splash">
            {sponsor?.logo_url ? (
              <img src={sponsor.logo_url} alt="" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }} className="max-h-24 max-w-[55%] object-contain" />
            ) : (
              <div className="text-5xl">⚽</div>
            )}
            <p className="mt-4 px-6 text-center text-base font-extrabold text-white">
              APL Penalty Cup{sponsor ? ` · Powered by ${sponsor.sponsor_name}` : ""}
            </p>
            <p className="mt-1 text-sm text-white/60">Get ready…</p>
          </div>
        )}
      </div>

      {/* footer (Surface E) */}
      <div className="px-4 py-2 text-center text-[11px] font-semibold text-white/60" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }} data-testid="game-footer">
        {footerText}
      </div>
    </div>
  );
}
